import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { shopProductService } from '@/services/shopProductService'
import type {
  Product,
  ProductListItem,
  ProductCategory,
  ProductImage,
  CreateProductRequest,
  UpdateProductRequest,
  UpdateInventoryRequest,
  CreateProductVariantRequest,
  UpdateProductVariantRequest,
  AddProductImageRequest,
  CreateProductCategoryRequest,
  UpdateProductCategoryRequest,
  CategoryOrderItem,
  ProductFilters,
  ProductImportRow,
  ProductImportResult
} from '@/types/shop'
import { isSessionExpiredError } from '@/services/api'

export const useProductStore = defineStore('product', () => {
  // ============================================================================
  // State
  // ============================================================================

  const products = ref<ProductListItem[]>([])
  const currentProduct = ref<Product | null>(null)
  const categories = ref<ProductCategory[]>([])
  const currentCategory = ref<ProductCategory | null>(null)
  const lowStockProducts = ref<ProductListItem[]>([])
  const outOfStockProducts = ref<ProductListItem[]>([])

  // Import state
  const importPreviewData = ref<ProductImportRow[]>([])
  const importResult = ref<ProductImportResult | null>(null)

  // Pagination & filtering
  const currentPage = ref(1)
  const pageSize = ref(20)
  const totalCount = ref(0)
  const totalVariantCount = ref(0)
  const totalPages = ref(0)
  const filters = ref<ProductFilters>({})

  // Loading states
  const loading = ref(false)
  const loadingProduct = ref(false)
  const loadingCategories = ref(false)
  const saving = ref(false)
  const importing = ref(false)

  // ============================================================================
  // Computed
  // ============================================================================

  const hasProducts = computed(() => products.value.length > 0)

  const hasCategories = computed(() => categories.value.length > 0)

  const activeCategories = computed(() =>
    categories.value.filter(c => c.isActive)
  )

  const productCount = computed(() => totalCount.value)

  const hasLowStock = computed(() => lowStockProducts.value.length > 0)

  const hasOutOfStock = computed(() => outOfStockProducts.value.length > 0)

  const validImportRows = computed(() =>
    importPreviewData.value.filter(r => r.status === 'valid')
  )

  const errorImportRows = computed(() =>
    importPreviewData.value.filter(r => r.status === 'error')
  )

  const warningImportRows = computed(() =>
    importPreviewData.value.filter(r => r.status === 'warning')
  )

  // ============================================================================
  // Product Actions
  // ============================================================================

  /**
   * Fetch products for a shop
   */
  async function fetchProducts(shopId: string, newFilters?: ProductFilters) {
    loading.value = true
    try {
      if (newFilters) {
        filters.value = { ...filters.value, ...newFilters }
      }
      const result = await shopProductService.getProducts(shopId, {
        ...filters.value,
        page: currentPage.value,
        pageSize: pageSize.value
      })
      products.value = result.items
      totalCount.value = result.totalCount
      totalVariantCount.value = result.totalVariantCount ?? 0
      totalPages.value = result.totalPages
      currentPage.value = result.page
    } catch (error) {
      if (!isSessionExpiredError(error)) {
        console.error('Error fetching products:', error)
      }
      throw error
    } finally {
      loading.value = false
    }
  }

  /**
   * Fetch product by ID
   */
  async function fetchProduct(id: string) {
    loadingProduct.value = true
    try {
      const product = await shopProductService.getProduct(id)
      currentProduct.value = product
      return product
    } catch (error) {
      if (!isSessionExpiredError(error)) {
        console.error('Error fetching product:', error)
      }
      throw error
    } finally {
      loadingProduct.value = false
    }
  }

  /**
   * Create a new product
   */
  async function createProduct(shopId: string, data: CreateProductRequest) {
    saving.value = true
    try {
      const product = await shopProductService.createProduct(shopId, data)
      // Add to products list with all required fields
      products.value.unshift({
        id: product.id,
        shopId: product.shopId,
        categoryId: product.categoryId,
        categoryName: product.categoryName,
        categoryFullPath: undefined, // Not available on Product type
        name: product.name,
        title: product.title || product.name,
        slug: product.slug,
        sku: product.sku,
        price: product.price,
        compareAtPrice: product.compareAtPrice,
        lowestVariantPrice: product.lowestVariantPrice ?? product.price,
        status: product.status,
        isFeatured: product.isFeatured,
        isExtra: product.isExtra,
        payableOnArrival: product.payableOnArrival ?? false,
        isVegan: product.isVegan ?? false,
        isVegetarian: product.isVegetarian ?? false,
        isHalal: product.isHalal ?? false,
        variantCount: product.variants?.length ?? 0,
        hasVariants: product.hasVariants,
        trackInventory: product.trackInventory,
        stockQuantity: product.stockQuantity,
        lowStockThreshold: product.lowStockThreshold,
        isInStock: product.trackInventory ? (product.stockQuantity ?? 0) > 0 : true,
        isLowStock: product.trackInventory ? (product.stockQuantity ?? 0) <= (product.lowStockThreshold ?? 0) && (product.stockQuantity ?? 0) > 0 : false,
        primaryImageUrl: product.images?.[0]?.imageUrl,
        sortOrder: product.sortOrder,
        createdAt: product.createdAt
      })
      totalCount.value++
      return product
    } catch (error) {
      console.error('Error creating product:', error)
      throw error
    } finally {
      saving.value = false
    }
  }

  /**
   * Update product
   */
  async function updateProduct(id: string, data: UpdateProductRequest) {
    saving.value = true
    try {
      const product = await shopProductService.updateProduct(id, data)
      // Update in products list
      const index = products.value.findIndex(p => p.id === id)
      if (index !== -1) {
        products.value[index] = {
          ...products.value[index],
          name: product.name,
          slug: product.slug,
          sku: product.sku,
          price: product.price,
          compareAtPrice: product.compareAtPrice,
          status: product.status,
          isFeatured: product.isFeatured,
          categoryId: product.categoryId,
          categoryName: product.categoryName,
          trackInventory: product.trackInventory,
          stockQuantity: product.stockQuantity,
          lowStockThreshold: product.lowStockThreshold,
          isInStock: product.trackInventory ? (product.stockQuantity ?? 0) > 0 : true,
          isLowStock: product.trackInventory ? (product.stockQuantity ?? 0) <= (product.lowStockThreshold ?? 0) && (product.stockQuantity ?? 0) > 0 : false
        }
      }
      if (currentProduct.value?.id === id) {
        currentProduct.value = product
      }
      return product
    } catch (error) {
      console.error('Error updating product:', error)
      throw error
    } finally {
      saving.value = false
    }
  }

  /**
   * Archive product
   */
  async function archiveProduct(id: string) {
    saving.value = true
    try {
      await shopProductService.archiveProduct(id)
      // Remove from products list
      products.value = products.value.filter(p => p.id !== id)
      totalCount.value--
      if (currentProduct.value?.id === id) {
        currentProduct.value = null
      }
    } catch (error) {
      console.error('Error archiving product:', error)
      throw error
    } finally {
      saving.value = false
    }
  }

  /**
   * Update product inventory
   */
  async function updateInventory(id: string, data: UpdateInventoryRequest) {
    saving.value = true
    try {
      const product = await shopProductService.updateInventory(id, data)
      // Update in products list
      const index = products.value.findIndex(p => p.id === id)
      if (index !== -1) {
        products.value[index].stockQuantity = product.stockQuantity
        products.value[index].lowStockThreshold = product.lowStockThreshold
        products.value[index].isInStock = product.trackInventory ? (product.stockQuantity ?? 0) > 0 : true
        products.value[index].isLowStock = product.trackInventory ? (product.stockQuantity ?? 0) <= (product.lowStockThreshold ?? 0) && (product.stockQuantity ?? 0) > 0 : false
      }
      if (currentProduct.value?.id === id) {
        currentProduct.value = product
      }
      return product
    } catch (error) {
      console.error('Error updating inventory:', error)
      throw error
    } finally {
      saving.value = false
    }
  }

  /**
   * Fetch low stock products
   */
  async function fetchLowStockProducts(shopId: string) {
    try {
      lowStockProducts.value = await shopProductService.getLowStockProducts(shopId)
    } catch (error) {
      if (!isSessionExpiredError(error)) {
        console.error('Error fetching low stock products:', error)
      }
      throw error
    }
  }

  /**
   * Fetch out of stock products
   */
  async function fetchOutOfStockProducts(shopId: string) {
    try {
      outOfStockProducts.value = await shopProductService.getOutOfStockProducts(shopId)
    } catch (error) {
      if (!isSessionExpiredError(error)) {
        console.error('Error fetching out of stock products:', error)
      }
      throw error
    }
  }

  // ============================================================================
  // Variant Actions
  // ============================================================================

  /**
   * Add variant to product
   */
  async function addVariant(productId: string, data: CreateProductVariantRequest) {
    saving.value = true
    try {
      const variant = await shopProductService.addVariant(productId, data)
      if (currentProduct.value?.id === productId) {
        currentProduct.value.variants.push(variant)
        currentProduct.value.hasVariants = true
      }
      return variant
    } catch (error) {
      console.error('Error adding variant:', error)
      throw error
    } finally {
      saving.value = false
    }
  }

  /**
   * Update variant
   */
  async function updateVariant(productId: string, variantId: string, data: UpdateProductVariantRequest) {
    saving.value = true
    try {
      const variant = await shopProductService.updateVariant(productId, variantId, data)
      if (currentProduct.value?.id === productId) {
        const index = currentProduct.value.variants.findIndex(v => v.id === variantId)
        if (index !== -1) {
          currentProduct.value.variants[index] = variant
        }
      }
      return variant
    } catch (error) {
      console.error('Error updating variant:', error)
      throw error
    } finally {
      saving.value = false
    }
  }

  /**
   * Delete variant
   */
  async function deleteVariant(productId: string, variantId: string) {
    saving.value = true
    try {
      await shopProductService.deleteVariant(productId, variantId)
      if (currentProduct.value?.id === productId) {
        currentProduct.value.variants = currentProduct.value.variants.filter(v => v.id !== variantId)
        currentProduct.value.hasVariants = currentProduct.value.variants.length > 0
      }
    } catch (error) {
      console.error('Error deleting variant:', error)
      throw error
    } finally {
      saving.value = false
    }
  }

  // ============================================================================
  // Image Actions
  // ============================================================================

  /**
   * Add image to product by URL
   */
  async function addImage(productId: string, data: AddProductImageRequest) {
    saving.value = true
    try {
      const image = await shopProductService.addImage(productId, data)
      if (currentProduct.value?.id === productId) {
        currentProduct.value.images.push(image)
      }
      return image
    } catch (error) {
      console.error('Error adding image:', error)
      throw error
    } finally {
      saving.value = false
    }
  }

  /**
   * Upload image file to product
   */
  async function uploadImage(productId: string, file: File, altText?: string) {
    saving.value = true
    try {
      const image = await shopProductService.uploadImage(productId, file, altText)
      if (currentProduct.value?.id === productId) {
        currentProduct.value.images.push(image)
      }
      return image
    } catch (error) {
      console.error('Error uploading image:', error)
      throw error
    } finally {
      saving.value = false
    }
  }

  /**
   * Remove image from product
   */
  async function removeImage(productId: string, imageId: string) {
    saving.value = true
    try {
      await shopProductService.removeImage(productId, imageId)
      if (currentProduct.value?.id === productId) {
        currentProduct.value.images = currentProduct.value.images.filter(i => i.id !== imageId)
      }
    } catch (error) {
      console.error('Error removing image:', error)
      throw error
    } finally {
      saving.value = false
    }
  }

  /**
   * Set an image as primary
   */
  async function setPrimaryImage(productId: string, imageId: string) {
    saving.value = true
    try {
      await shopProductService.setPrimaryImage(imageId)
      if (currentProduct.value?.id === productId) {
        currentProduct.value.images = currentProduct.value.images.map(i => ({
          ...i,
          isPrimary: i.id === imageId
        }))
      }
    } catch (error) {
      console.error('Error setting primary image:', error)
      throw error
    } finally {
      saving.value = false
    }
  }

  /**
   * Reorder product images
   */
  async function reorderImages(productId: string, imageIds: string[]) {
    saving.value = true
    try {
      await shopProductService.reorderImages(productId, { imageIds })
      if (currentProduct.value?.id === productId) {
        // Reorder images locally
        const reordered = imageIds.map((id, index) => {
          const image = currentProduct.value!.images.find(i => i.id === id)
          if (image) {
            image.sortOrder = index
            image.isPrimary = index === 0
          }
          return image
        }).filter(Boolean) as ProductImage[]
        currentProduct.value.images = reordered
      }
    } catch (error) {
      console.error('Error reordering images:', error)
      throw error
    } finally {
      saving.value = false
    }
  }

  // ============================================================================
  // Category Actions
  // ============================================================================

  /**
   * Fetch categories for a shop (tree structure for category management)
   */
  async function fetchCategories(shopId: string) {
    loadingCategories.value = true
    try {
      categories.value = await shopProductService.getCategories(shopId)
    } catch (error) {
      if (!isSessionExpiredError(error)) {
        console.error('Error fetching categories:', error)
      }
      throw error
    } finally {
      loadingCategories.value = false
    }
  }

  /**
   * Fetch categories as flat list with full path (for dropdown selection)
   */
  async function fetchCategoriesFlat(shopId: string) {
    loadingCategories.value = true
    try {
      categories.value = await shopProductService.getCategoriesFlat(shopId)
    } catch (error) {
      if (!isSessionExpiredError(error)) {
        console.error('Error fetching categories:', error)
      }
      throw error
    } finally {
      loadingCategories.value = false
    }
  }

  /**
   * Fetch category by ID
   */
  async function fetchCategory(id: string) {
    loadingCategories.value = true
    try {
      const category = await shopProductService.getCategory(id)
      currentCategory.value = category
      return category
    } catch (error) {
      if (!isSessionExpiredError(error)) {
        console.error('Error fetching category:', error)
      }
      throw error
    } finally {
      loadingCategories.value = false
    }
  }

  /**
   * Create a new category
   */
  async function createCategory(shopId: string, data: CreateProductCategoryRequest) {
    saving.value = true
    try {
      const category = await shopProductService.createCategory(shopId, data)
      categories.value.push(category)
      return category
    } catch (error) {
      console.error('Error creating category:', error)
      throw error
    } finally {
      saving.value = false
    }
  }

  /**
   * Update category
   */
  async function updateCategory(id: string, data: UpdateProductCategoryRequest) {
    saving.value = true
    try {
      const category = await shopProductService.updateCategory(id, data)
      const index = categories.value.findIndex(c => c.id === id)
      if (index !== -1) {
        categories.value[index] = category
      }
      if (currentCategory.value?.id === id) {
        currentCategory.value = category
      }
      return category
    } catch (error) {
      console.error('Error updating category:', error)
      throw error
    } finally {
      saving.value = false
    }
  }

  /**
   * Delete category
   */
  async function deleteCategory(id: string) {
    saving.value = true
    try {
      await shopProductService.deleteCategory(id)
      categories.value = categories.value.filter(c => c.id !== id)
      if (currentCategory.value?.id === id) {
        currentCategory.value = null
      }
    } catch (error) {
      console.error('Error deleting category:', error)
      throw error
    } finally {
      saving.value = false
    }
  }

  /**
   * Reorder categories
   */
  async function reorderCategories(shopId: string, orderItems: CategoryOrderItem[]) {
    saving.value = true
    try {
      await shopProductService.reorderCategories(shopId, orderItems)
      // Update local order
      orderItems.forEach(item => {
        const category = categories.value.find(c => c.id === item.categoryId)
        if (category) {
          category.sortOrder = item.sortOrder
        }
      })
      categories.value.sort((a, b) => a.sortOrder - b.sortOrder)
    } catch (error) {
      console.error('Error reordering categories:', error)
      throw error
    } finally {
      saving.value = false
    }
  }

  // ============================================================================
  // Bulk Import Actions
  // ============================================================================

  /**
   * Parse CSV file for preview
   */
  async function parseImportFile(file: File) {
    importing.value = true
    try {
      const rows = await shopProductService.parseCSVFile(file)
      importPreviewData.value = rows
      return rows
    } catch (error) {
      console.error('Error parsing import file:', error)
      throw error
    } finally {
      importing.value = false
    }
  }

  /**
   * Execute bulk import
   */
  async function executeBulkImport(shopId: string, skipErrors: boolean = false) {
    importing.value = true
    try {
      const products = validImportRows.value.map(row => ({
        categoryId: undefined, // Will need to resolve category by name
        name: row.name,
        description: row.description,
        shortDescription: row.shortDescription,
        sku: row.sku,
        barcode: row.barcode,
        price: row.price,
        compareAtPrice: row.compareAtPrice,
        costPrice: row.costPrice,
        isFeatured: row.isFeatured,
        trackInventory: row.trackInventory,
        stockQuantity: row.stockQuantity,
        lowStockThreshold: row.lowStockThreshold,
        unitOfMeasure: row.unitOfMeasure,
        weight: row.weight,
        weightUnit: row.weightUnit,
        imageUrls: row.imageUrls
      } as CreateProductRequest))

      const result = await shopProductService.bulkCreateProducts(shopId, {
        products,
        skipErrors
      })
      importResult.value = result
      return result
    } catch (error) {
      console.error('Error executing bulk import:', error)
      throw error
    } finally {
      importing.value = false
    }
  }

  /**
   * Download CSV template
   */
  function downloadTemplate() {
    shopProductService.downloadCSVTemplate()
  }

  /**
   * Clear import data
   */
  function clearImportData() {
    importPreviewData.value = []
    importResult.value = null
  }

  // ============================================================================
  // Pagination & Filtering
  // ============================================================================

  function setPage(page: number, shopId: string) {
    currentPage.value = page
    fetchProducts(shopId)
  }

  function setPageSize(size: number, shopId: string) {
    pageSize.value = size
    currentPage.value = 1
    fetchProducts(shopId)
  }

  function setFilters(newFilters: ProductFilters, shopId: string) {
    filters.value = newFilters
    currentPage.value = 1
    fetchProducts(shopId)
  }

  function clearFilters(shopId: string) {
    filters.value = {}
    currentPage.value = 1
    fetchProducts(shopId)
  }

  // ============================================================================
  // Reset
  // ============================================================================

  function resetStore() {
    products.value = []
    currentProduct.value = null
    categories.value = []
    currentCategory.value = null
    lowStockProducts.value = []
    outOfStockProducts.value = []
    importPreviewData.value = []
    importResult.value = null
    currentPage.value = 1
    totalCount.value = 0
    totalVariantCount.value = 0
    totalPages.value = 0
    filters.value = {}
  }

  return {
    // State
    products,
    currentProduct,
    categories,
    currentCategory,
    lowStockProducts,
    outOfStockProducts,
    importPreviewData,
    importResult,
    currentPage,
    pageSize,
    totalCount,
    totalVariantCount,
    totalPages,
    filters,
    loading,
    loadingProduct,
    loadingCategories,
    saving,
    importing,

    // Computed
    hasProducts,
    hasCategories,
    activeCategories,
    productCount,
    hasLowStock,
    hasOutOfStock,
    validImportRows,
    errorImportRows,
    warningImportRows,

    // Product Actions
    fetchProducts,
    fetchProduct,
    createProduct,
    updateProduct,
    archiveProduct,
    updateInventory,
    fetchLowStockProducts,
    fetchOutOfStockProducts,

    // Variant Actions
    addVariant,
    updateVariant,
    deleteVariant,

    // Image Actions
    addImage,
    uploadImage,
    removeImage,
    reorderImages,
    setPrimaryImage,

    // Category Actions
    fetchCategories,
    fetchCategoriesFlat,
    fetchCategory,
    createCategory,
    updateCategory,
    deleteCategory,
    reorderCategories,

    // Bulk Import Actions
    parseImportFile,
    executeBulkImport,
    downloadTemplate,
    clearImportData,

    // Pagination & Filtering
    setPage,
    setPageSize,
    setFilters,
    clearFilters,

    // Reset
    resetStore
  }
})
